import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';

/**
 * Native iOS-style Bottom Sheet component
 * Uses framer-motion for buttery smooth animations and gestures
 * Uses portal to render above all other content
 */
export default function BottomSheet({
  isOpen,
  onClose,
  children,
  title,
  snapPoints = ['90%', '50%'], // Array of snap points
  initialSnap = 1, // Index of initial snap point
  showDragIndicator = true,
  closeOnBackdrop = true,
}) {
  const dragControls = useDragControls();

  // Lock body scroll when open - only use overflow, not touchAction
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeOnBackdrop ? onClose : undefined}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 9998,
            }}
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              // Close if dragged down fast enough or past threshold
              if (info.velocity.y > 500 || info.offset.y > 150) {
                onClose();
              }
            }}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              // Use dvh for dynamic viewport on iOS
              // Account for top safe area (notch) with env()
              // Both min and max set to same value for consistent height
              height: 'calc(100dvh - env(safe-area-inset-top, 0px) - 20px)',
              maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 20px)',
              background: '#0f0f0f',
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
            }}
          >
            {/* Drag indicator - only this triggers drag */}
            {showDragIndicator && (
              <div
                onPointerDown={(e) => dragControls.start(e)}
                style={{
                  padding: '12px 0 8px',
                  display: 'flex',
                  justifyContent: 'center',
                  cursor: 'grab',
                  touchAction: 'none',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 4,
                    borderRadius: 2,
                    background: 'rgba(255,255,255,0.2)',
                  }}
                />
              </div>
            )}

            {/* Header */}
            {title && (
              <div
                style={{
                  padding: '8px 20px 16px',
                  borderBottom: '1px solid rgba(255,255,255,0.08)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 600,
                    color: '#fff',
                  }}
                >
                  {title}
                </h2>
                {/* Close button - always visible */}
                <button
                  onClick={onClose}
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.1)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                  aria-label="Close"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            {/* Content - scrollable */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
              }}
            >
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
